import { WifiOff } from 'lucide-react';

import { useOfflineBanner } from '@/hooks/ui/banners/useOfflineBanner';
import { PageBanner } from './PageBanner';

/**
 * The offline strip — ROUTE_INVENTORY Part 1, G6. Tone: warning (the app is
 * not broken, the connection is; it clears itself on reconnect).
 *
 * `OfflineBannerView` is the strip; `OfflineBanner` is the strip wired to its
 * signals (`useOfflineBanner`), standalone. In the app it is shown by
 * `features/shell/ShellBanners`, which picks ONE page-wide strip by priority.
 *
 * `onDismiss` is passed on a phone only, where the banner floats OVER the
 * page: a card that could not be closed would sit on top of it for as long
 * as the connection stayed down. Beside `sm` it is a strip in the flow and
 * clears itself on reconnect, as before.
 */
export function OfflineBannerView({ onDismiss }: { onDismiss?: () => void }) {
  return (
    <PageBanner
      tone='warning'
      icon={WifiOff}
      message="You're offline."
      detail="Some things won't work until you reconnect."
      onDismiss={onDismiss}
      dismissLabel='Dismiss offline notice'
    />
  );
}

/**
 * Polite, and PERMANENT: the `role='status'` region stays mounted, empty, so
 * the message is announced when it arrives — a polite region inserted already
 * filled is not reliably read out. (The urgent strips use `role='alert'`,
 * which is announced on insertion, and can mount whole.)
 */
export function OfflineStatusRegion({
  show,
  onDismiss,
}: {
  show: boolean;
  onDismiss?: () => void;
}) {
  return (
    <div role='status' aria-live='polite'>
      {show && <OfflineBannerView onDismiss={onDismiss} />}
    </div>
  );
}

export function OfflineBanner() {
  const { raised } = useOfflineBanner();
  return <OfflineStatusRegion show={raised} />;
}
